"use client";

import React, { useState, useEffect } from "react";
import { MapPin, Star, Quote, ArrowRight } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious, type CarouselApi } from "@/components/ui/carousel";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const stories = [{
  town: "Great Yarmouth",
  rating: 5,
  quote: "AngliaKeys arrived quickly and got me back on the road in no time. The new key works perfectly!",
  author: "Jane D.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1573441/img/rescue_story_great_yarmouth_keys.png",
  service: "Lost Key Replacement"
}, {
  town: "Norwich",
  rating: 5,
  quote: "Excellent service for lost keys. Very professional and friendly team. Saved me a trip to the dealer.",
  author: "Mark S.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1573441/img/rescue_story_norwich_transponder.png",
  service: "Transponder Programming"
}, {
  town: "King's Lynn",
  rating: 5,
  quote: "Locked my keys in the boot at the supermarket. They arrived in 20 minutes and picked the lock without a scratch.",
  author: "Sarah P.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1573441/img/rescue_story_kings_lynn_lockout.png",
  service: "Emergency Lockout"
}, {
  town: "Cromer",
  rating: 5,
  quote: "Snapped my key in the ignition. They extracted the broken piece and cut a new one right there on the roadside.",
  author: "David W.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1573441/img/rescue_story_cromer_ignition.png",
  service: "Broken Key Extraction"
}];
export default function RecentRoadsideRescues() {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  useEffect(() => {
    if (!api) return;
    setCount(api.scrollSnapList().length);
    setCurrent(api.selectedScrollSnap() + 1);
    api.on("select", () => {
      setCurrent(api.selectedScrollSnap() + 1);
    });
  }, [api]);
  return <section id="recent-roadside-rescues" ref={ref} className={`py-24 bg-background relative overflow-hidden transition-opacity duration-700 ${isVisible ? "opacity-100" : "opacity-0"}`}>
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] right-[-5%] w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-[-10%] left-[-5%] w-96 h-96 bg-secondary/5 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <Badge variant="outline" className="mb-4 border-primary/50 text-primary-foreground bg-primary/10">
            Real Stories
          </Badge>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6 text-foreground">
            Recent Roadside <span className="text-primary">Rescues</span>
          </h2>
          <p className="text-lg text-muted-foreground">
            From emergency lockouts in Norwich to lost keys in Great Yarmouth, see how we get East Anglia's drivers back on the road.
          </p>
        </div>

        <div className="max-w-6xl mx-auto">
          <Carousel setApi={setApi} className="w-full" opts={{
          loop: true
        }}>
            <CarouselContent>
              {stories.map((story, index) => <CarouselItem key={index} data-index={index} className="md:basis-1/1 lg:basis-1/1">
                  <div className="p-1">
                    <Card className="border-border/50 shadow-lg bg-card/50 backdrop-blur-sm overflow-hidden">
                      <CardContent className="p-0">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
                          {}
                          <div className="p-8 md:p-12 flex flex-col justify-center order-2 lg:order-1">
                            <div className="flex items-center gap-2 mb-6">
                              <Badge variant="secondary" className="flex items-center gap-1 px-3 py-1 text-sm">
                                <MapPin className="w-3.5 h-3.5" />
                                {story.town}
                              </Badge>
                              <span className="text-muted-foreground text-sm">•</span>
                              <span className="text-muted-foreground text-sm font-medium">{story.service}</span>
                            </div>

                            <div className="flex gap-1 mb-6">
                              {[...Array(5)].map((_, i) => <Star key={i} className={`w-5 h-5 ${i < story.rating ? "fill-primary text-primary" : "fill-muted text-muted"}`} />)}
                            </div>

                            <blockquote className="relative mb-8">
                              <Quote className="absolute -top-4 -left-2 w-8 h-8 text-primary/20 rotate-180" />
                              <p className="text-xl md:text-2xl font-medium leading-relaxed text-foreground relative z-10 pl-6">
                                "{story.quote}"
                              </p>
                            </blockquote>

                            <div className="flex items-center gap-4 mt-auto">
                              <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center text-secondary-foreground font-bold">
                                {story.author.charAt(0)}
                              </div>
                              <div>
                                <div className="font-semibold text-foreground">{story.author}</div>
                                <div className="text-sm text-muted-foreground">Verified Customer</div>
                              </div>
                            </div>
                          </div>

                          {}
                          <div className="relative h-64 lg:h-auto min-h-[300px] bg-muted order-1 lg:order-2 overflow-hidden group">
                            <img src={story.image} alt={`Before and after key replacement in ${story.town}`} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent lg:bg-gradient-to-l lg:from-transparent lg:to-black/10" />
                            
                            <div className="absolute bottom-4 right-4 bg-black/70 backdrop-blur-md text-white text-xs font-bold px-3 py-1.5 rounded-full border border-white/10 shadow-xl">
                              Before & After
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </CarouselItem>)}
            </CarouselContent>
            
            <div className="hidden md:block">
              <CarouselPrevious className="left-[-2rem] lg:left-[-3rem] bg-background border-border hover:bg-primary hover:text-primary-foreground hover:border-primary transition-colors" />
              <CarouselNext className="right-[-2rem] lg:right-[-3rem] bg-background border-border hover:bg-primary hover:text-primary-foreground hover:border-primary transition-colors" />
            </div>

            {}
            <div className="flex justify-center gap-2 mt-8">
              {Array.from({
              length: count
            }).map((_, index) => <Button key={index} variant="ghost" size="sm" onClick={() => api?.scrollTo(index)} className={`p-0 h-2 rounded-full transition-all duration-300 hover:bg-transparent ${current === index + 1 ? "w-8 bg-primary hover:bg-primary" : "w-2 bg-muted-foreground/30 hover:bg-muted-foreground/50"}`} aria-label={`Go to slide ${index + 1}`} />)}
            </div>
          </Carousel>
        </div>

        <div className="mt-12 text-center">
          <Button asChild size="lg" className="font-semibold text-base">
            <Link to="/AreasCoveredPage">
              Check Your Area <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}